/**
 * Mahlgrad-Auswahl: traegt den gewaehlten Mahlgrad als Positionseigenschaft in
 * den Warenkorb.
 *
 * Der Block liegt neben dem Produktformular, nicht darin, deshalb zwei Wege:
 *
 *   1. Ein verstecktes properties[]-Feld im Formular. Deckt den FormData-Pfad in
 *      ProductFormComponent#processAddToCart ab, ohne Eingriff in Vendor-Code.
 *   2. getMahlgradProperties(), gelesen von #processBatchAddToCart. Adds, die
 *      waehrend eines Variantenwechsels in die Queue laufen, senden keine
 *      Formularfelder — dort ist Weg 1 wirkungslos.
 */
import { onDocumentReady } from '@theme/utilities';

const PROPERTY = 'Mahlgrad';

/**
 * Vorbelegung, falls der Waehler keinen Wert liefert. Der Block setzt selected
 * bereits im Markup; das hier ist die zweite Sicherung, damit die Position
 * nicht ohne Mahlgrad in die Bestellung geht.
 *
 * Bewusst nur wirksam, WENN ein Waehler vorhanden ist - siehe
 * getMahlgradProperties. Sonst bekaemen Tee und Zubehoer einen Mahlgrad
 * angehaengt, den es dort nicht gibt.
 */
const VORGABE = 'Ganze Bohne';
const SECTION_SELECTOR = '.shopify-section, [id^="shopify-section-"]';

/**
 * Sucht im selben Section-Scope, damit ein Quick-Add fuer ein anderes Produkt
 * auf derselben Seite nicht den Mahlgrad dieser PDP bekommt.
 * @param {Element | null | undefined} origin
 * @returns {Element}
 */
function scopeOf(origin) {
  return origin?.closest?.(SECTION_SELECTOR) ?? document.documentElement;
}

/**
 * @param {Element} scope
 * @returns {HTMLSelectElement | null}
 */
function selectIn(scope) {
  return /** @type {HTMLSelectElement | null} */ (scope.querySelector('[data-custom-mahlgrad-select]'));
}

/**
 * @param {Element} scope
 * @returns {HTMLFormElement | null}
 */
function formIn(scope) {
  const selector = 'form[action*="/cart/add"]';

  return /** @type {HTMLFormElement | null} */ (
    scope.querySelector(selector) ?? document.querySelector(selector)
  );
}

/**
 * Positionseigenschaften fuer einen Add-to-Cart aus diesem Section-Scope, oder
 * null wenn dort kein Mahlgrad gewaehlt werden kann.
 * @param {Element | null} [origin] - Das ProductFormComponent-Element.
 * @returns {Record<string, string> | null}
 */
export function getMahlgradProperties(origin) {
  const select = selectIn(scopeOf(origin));

  // Kein Waehler heisst: das Produkt kennt keinen Mahlgrad. Dann auch keine
  // Positionseigenschaft - nicht die Vorgabe.
  if (!select) return null;

  return { [PROPERTY]: select.value || VORGABE };
}

/**
 * Haelt das versteckte Feld im Formular auf dem Stand des Selects.
 * @param {Element} picker
 */
function syncHiddenInput(picker) {
  const scope = scopeOf(picker);
  const select = selectIn(scope);
  const form = formIn(scope);

  if (!select || !form) return;

  let input = form.querySelector('input[data-custom-mahlgrad-input]');

  if (!(input instanceof HTMLInputElement)) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.dataset.customMahlgradInput = '';
    form.appendChild(input);
  }

  input.name = `properties[${PROPERTY}]`;
  input.value = select.value || VORGABE;
}

/**
 * @param {Element} picker
 */
function initPicker(picker) {
  if (!(picker instanceof HTMLElement) || picker.dataset.customMahlgradReady === 'true') return;

  picker.dataset.customMahlgradReady = 'true';

  const select = selectIn(scopeOf(picker));

  if (!select) return;

  syncHiddenInput(picker);
  select.addEventListener('change', () => syncHiddenInput(picker));

  // Das Theme rendert das Formular beim Variantenwechsel neu und verwirft das
  // Feld dabei, deshalb kurz vor dem Absenden erneut setzen.
  const form = formIn(scopeOf(picker));

  if (!form) return;

  const restore = () => syncHiddenInput(picker);

  form.addEventListener('submit', restore, { capture: true });
  form.addEventListener('click', restore, { capture: true });
}

function init() {
  for (const picker of document.querySelectorAll('[data-custom-mahlgrad]')) {
    initPicker(picker);
  }
}

onDocumentReady(init, { once: true });
document.addEventListener('shopify:section:load', init);
